import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { DonationClubView } from "@/pages/DonationClub";
import { ExportUsersButton } from "@/components/ExportUsersButton";

// Tailwind color class for a request status (avoids nested ternaries at call sites).
function statusColor(status) {
  if (status === "approved") return "text-emerald-400";
  if (status === "rejected") return "text-red-400";
  return "text-amber-400";
}

// Display labels for admin tabs that differ from their raw key.
const TAB_LABELS = {
  credit: "Send fund",
  deposits: "Fund Requests",
  bep20: "BEP20 Addresses",
  starclub: "Global Club",
  donation: "Donation Club",
  achievers: "Achievers",
  playbox: "Play Box",
  usertxns: "User Transactions",
  system: "System",
};

export default function AdminPanel() {
  const [tab, setTab] = useState("activations");
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [activations, setActivations] = useState([]);
  const [stats, setStats] = useState(null);
  const [credit, setCredit] = useState({ user_id: "", amount: 100, wallet: "deposit" });
  const [topupAmount, setTopupAmount] = useState(100);
  const [transfers, setTransfers] = useState([]);
  const [lpHistory, setLpHistory] = useState([]);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [resetTarget, setResetTarget] = useState(null); // {user_id, full_name}
  const [resetForm, setResetForm] = useState({ new_password: "", new_t_pass: "", reset_tpass: false });
  const [resetMsg, setResetMsg] = useState({ type: "", text: "" });
  const [scLookup, setScLookup] = useState("");
  const [scData, setScData] = useState(null);
  const [scMsg, setScMsg] = useState({ type: "", text: "" });
  const [scLoading, setScLoading] = useState(false);
  const [dcLookup, setDcLookup] = useState("");
  const [dcData, setDcData] = useState(null);
  const [dcMsg, setDcMsg] = useState({ type: "", text: "" });
  const [dcLoading, setDcLoading] = useState(false);
  const [achievers, setAchievers] = useState([]);
  const [achClub, setAchClub] = useState("");
  const [achLoading, setAchLoading] = useState(false);
  const [fundRequests, setFundRequests] = useState([]);
  const [previewImg, setPreviewImg] = useState(null);
  const [bep20Rows, setBep20Rows] = useState([]);
  const [bep20Search, setBep20Search] = useState("");
  const [bep20Edits, setBep20Edits] = useState({});
  const [fundWd, setFundWd] = useState(null);          // { fund, label }
  const [fundWdForm, setFundWdForm] = useState({ amount: "", reason: "", t_pass: "" });
  const [fundWdBusy, setFundWdBusy] = useState(false);
  const [fundWdMsg, setFundWdMsg] = useState("");
  const openFundWd = (fund, label) => { setFundWd({ fund, label }); setFundWdForm({ amount: "", reason: "", t_pass: "" }); setFundWdMsg(""); };
  const submitFundWd = async (e) => {
    e.preventDefault();
    setFundWdBusy(true); setFundWdMsg("");
    try {
      const r = await api.post("/admin/system-fund/deduct", {
        fund: fundWd.fund, amount: Number(fundWdForm.amount),
        reason: fundWdForm.reason || null, t_pass: fundWdForm.t_pass,
      });
      setFundWdMsg(`✓ Withdrew $${r.data.deducted.toFixed(2)}. New balance: $${r.data.balance.toFixed(2)}.`);
      setFundWdForm({ amount: "", reason: "", t_pass: "" });
      await load();
      setTimeout(() => { setFundWd(null); setFundWdMsg(""); }, 1300);
    } catch (err) {
      setFundWdMsg(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setFundWdBusy(false); }
  };
  const [bep20Copied, setBep20Copied] = useState(null);
  const copyBep20 = async (userNo, addr) => {
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(addr);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = addr; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setBep20Copied(userNo);
    setTimeout(() => setBep20Copied((c) => (c === userNo ? null : c)), 1500);
  };

  const load = useCallback(async () => {
    try {
      const [u, w, a, s, ft, lp, fr, bep] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/withdrawals"),
        api.get("/admin/activation-requests"),
        api.get("/admin/stats"),
        api.get("/admin/fund-transfers"),
        api.get("/admin/lp-history"),
        api.get("/admin/fund-requests"),
        api.get("/admin/bep20-addresses"),
      ]);
      setUsers(u.data.users);
      setWithdrawals(w.data.rows);
      setActivations(a.data.rows);
      setStats(s.data);
      setTransfers(ft.data.rows);
      setLpHistory(lp.data.rows);
      setFundRequests(fr.data.rows);
      setBep20Rows(bep.data.rows);
    } catch (err) {
      console.warn("Admin data load failed:", err?.message);
    }
  }, []);

  const saveBep20 = async (user_no) => {
    const address = (bep20Edits[user_no] ?? "").trim();
    try {
      await api.post("/admin/bep20-address", { user_no, address });
      await load();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => {
    window.setTimeout(load, 0);
  }, [load]);

  const systemFundEntries = useMemo(
    () => Object.entries(stats?.system_funds || {}).filter(([k]) => k !== "_id"),
    [stats]
  );

  const creditUserName = useMemo(() => {
    const id = (credit.user_id || "").trim();
    if (!id) return null;
    const u = users.find((x) => String(x.user_id) === id);
    return u ? u.full_name : "__not_found__";
  }, [credit.user_id, users]);

  const doTopup = async (e) => {
    e.preventDefault(); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/add-fund", { amount: Number(topupAmount) });
      setMsg({ type: "ok", text: `Added $${Number(topupAmount).toFixed(2)} to Platform Fee. New balance: $${Number(r.data.balance).toFixed(2)}` });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const doCredit = async (e) => {
    e.preventDefault(); setMsg({ type: "", text: "" });
    try {
      await api.post("/admin/credit", {
        user_id: credit.user_id, amount: Number(credit.amount), wallet: credit.wallet,
      });
      setMsg({ type: "ok", text: `Credited $${credit.amount} (${credit.wallet}) to ${credit.user_id}` });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const reverseTransfer = async (id) => {
    if (!window.confirm("Reverse this transfer? The amount will be clawed back from the recipient's wallet and returned to the Platform Fee.")) return;
    setMsg({ type: "", text: "" });
    try {
      const r = await api.post(`/admin/fund-transfers/${id}/reverse`);
      setMsg({ type: "ok", text: `Reversed $${Number(r.data.amount).toFixed(2)} back to Platform Fee.` });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const act = async (id, action) => {
    try { await api.post(`/admin/withdrawals/${id}/${action}`); await load(); }
    catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const actActivation = async (id, action) => {
    try { await api.post(`/admin/activation-requests/${id}/${action}`); await load(); }
    catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const actFund = async (id, action) => {
    if (action === "reject" && !window.confirm("Reject this fund request? No funds will be credited.")) return;
    try { await api.post(`/admin/fund-requests/${id}/${action}`); await load(); }
    catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openReset = (u) => {
    setResetTarget({ user_id: u.user_id, full_name: u.full_name });
    setResetForm({ new_password: "", new_t_pass: "", reset_tpass: false });
    setResetMsg({ type: "", text: "" });
  };
  const closeReset = () => setResetTarget(null);
  const submitReset = async (e) => {
    e.preventDefault(); setResetMsg({ type: "", text: "" });
    try {
      const payload = { user_id: resetTarget.user_id, new_password: resetForm.new_password };
      if (resetForm.reset_tpass && resetForm.new_t_pass) payload.new_t_pass = resetForm.new_t_pass;
      await api.post("/admin/reset-password", payload);
      setResetMsg({ type: "ok", text: `Password reset for ${resetTarget.user_id}.` });
      setTimeout(() => closeReset(), 1200);
    } catch (e) {
      setResetMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const loadStarClub = async (eOrId) => {
    let lookup = scLookup;
    if (eOrId && typeof eOrId === "string") {
      lookup = eOrId;
    } else if (eOrId && eOrId.preventDefault) {
      eOrId.preventDefault();
    }
    if (!lookup.trim()) return;
    setScLoading(true); setScMsg({ type: "", text: "" }); setScData(null);
    try {
      const r = await api.get(`/admin/star-club/${lookup.trim()}`);
      setScData(r.data);
    } catch (err) {
      setScMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally { setScLoading(false); }
  };

  const loadDonationClub = async (eOrId) => {
    let lookup = dcLookup;
    if (eOrId && typeof eOrId === "string") {
      lookup = eOrId;
    } else if (eOrId && eOrId.preventDefault) {
      eOrId.preventDefault();
    }
    if (!lookup.trim()) return;
    setDcLoading(true); setDcMsg({ type: "", text: "" }); setDcData(null);
    try {
      const r = await api.get(`/admin/donation-club/${lookup.trim()}`);
      setDcData(r.data);
    } catch (err) {
      setDcMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally { setDcLoading(false); }
  };

  const loadAchievers = useCallback(async (clubFilter) => {
    setAchLoading(true);
    try {
      const url = clubFilter ? `/admin/achievers?club=${clubFilter}` : "/admin/achievers";
      const r = await api.get(url);
      setAchievers(r.data.achievers || []);
    } catch (err) {
      console.warn("Achievers load failed:", err?.message);
      setAchievers([]);
    } finally { setAchLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === "achievers") {
      window.setTimeout(() => loadAchievers(achClub || null), 0);
    }
  }, [tab, achClub, loadAchievers]);

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Administration</div>
        <h1 className="font-display text-4xl mt-1">Control Room</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
          <div className="lt-card p-5">
            <div className="overline">Admin Fund</div>
            <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="stat-admin-fund">${(stats.platform_fee || 0).toFixed(2)}</div>
            <div className="text-xs text-neutral-500 mt-1">Spendable balance</div>
          </div>
          <div className="lt-card p-5" data-testid="stat-creator-fund-card">
            <div className="overline">Creator fund</div>
            <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="stat-creator-fund">
              ${Number(stats?.system_funds?.creator_wallet || 0).toFixed(2)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">5% Subscription + 2% Play Growth</div>
            <button onClick={() => openFundWd("creator_wallet", "Creator Fund")} className="mt-3 text-xs gold-text hover:underline" data-testid="creator-fund-withdraw-btn">Manual withdrawal →</button>
          </div>
          <div className="lt-card p-5" data-testid="stat-lp-pool-card">
            <div className="overline">LP / Liquidity Pool</div>
            <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="stat-lp-pool">
              ${Number(stats?.system_funds?.lp_pool || 0).toFixed(2)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">40% Sub + 2.2% Play Growth + $5 / Cycle</div>
          </div>
          <div className="lt-card p-5" data-testid="stat-system-fund-card">
            <div className="overline">System fund</div>
            <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="stat-system-fund">
              ${Number(stats?.system_funds?.system_fund || 0).toFixed(2)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Unallocated team/royalty/play leftovers</div>
            <button onClick={() => openFundWd("system_fund", "System Fund")} className="mt-3 text-xs gold-text hover:underline" data-testid="system-fund-withdraw-btn">Manual withdrawal →</button>
          </div>
          <div className="lt-card p-5"><div className="overline">Pending activations</div>
            <div className="font-display text-3xl tabular mt-2 text-amber-400" data-testid="stat-pending-activations">{stats.pending_activations || 0}</div>
          </div>
          <div className="lt-card p-5"><div className="overline">Total users</div><div className="font-display text-3xl tabular mt-2" data-testid="stat-total-users">{stats.total_users}</div></div>
          <div className="lt-card p-5"><div className="overline">Active subscribers</div><div className="font-display text-3xl tabular mt-2">{stats.active_subscribers}</div></div>
          <div className="lt-card p-5"><div className="overline">Pending withdrawals</div><div className="font-display text-3xl tabular mt-2">{stats.pending_withdrawals}</div></div>
          <div className="lt-card p-5"><div className="overline">Total revenue</div><div className="font-display text-3xl tabular mt-2 gold-text">${stats.total_subscription_revenue.toFixed(2)}</div></div>
          <div className="lt-card p-5" data-testid="stat-star-club-fund-card">
            <div className="overline">Global Club Fund</div>
            <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="stat-star-club-fund">
              ${Number(stats.star_club_fund || 0).toFixed(2)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Lifetime Global Club payouts (Main + Rebirth)</div>
          </div>
          <div className="lt-card p-5" data-testid="stat-game-fund-card">
            <div className="overline">Game Fund</div>
            <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="stat-game-fund">
              ${Number(stats.game_fund || 0).toFixed(2)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              Locked principal in active boxes (${Number(stats.game_fund_locked || 0).toFixed(2)}) + claimable (${Number(stats.game_fund_claimable || 0).toFixed(2)})
            </div>
          </div>
          <div className="lt-card p-5" data-testid="stat-user-main-total-card">
            <div className="overline">User Main Wallet Balance</div>
            <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="stat-user-main-total">
              ${Number(stats.user_main_total || 0).toFixed(2)}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Sum of all members&apos; main wallets (view only)</div>
          </div>
        </div>
      )}

      {stats?.system_funds && (
        <div className="lt-card p-6">
          <div className="overline mb-3">System Funds</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {systemFundEntries.map(([k, v]) => {
              // `admin_wallet` is a deprecated phantom field. This System Funds "Platform Fee"
              // row shows the cumulative 10% fee collected from users (stats.admin_fee_collected),
              // NOT the admin's spendable balance.
              const value = k === "admin_wallet" ? (stats.admin_fee_collected || 0) : v;
              return (
                <div key={k}>
                  <div className="overline">{k === "admin_wallet" ? "Platform Fee" : k.replace(/_/g, " ")}</div>
                  <div className="font-display text-2xl tabular mt-1">${Number(value || 0).toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {["activations", "deposits", "users", "credit", "withdrawals", "bep20", "starclub", "donation", "achievers", "playbox", "usertxns", "system"].map((t) => {
          let badge = 0;
          if (t === "activations") badge = activations.filter((a) => a.status === "pending").length;
          else if (t === "deposits") badge = fundRequests.filter((f) => f.status === "pending").length;
          const label = TAB_LABELS[t] || t;
          return (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-4 py-2 text-sm capitalize rounded flex items-center gap-2 ${tab === t ? "bg-[#D4AF37] text-black" : "lt-btn-secondary"}`}>
              {label}
              {badge > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${tab === t ? "bg-black text-[#D4AF37]" : "bg-amber-500/20 text-amber-300"}`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "activations" && (
        <div className="lt-card p-6 overflow-x-auto" data-testid="activations-table">
          <div className="overline mb-3">Package activation requests</div>
          <table className="lt-table">
            <thead><tr><th>When</th><th>User</th><th>Package</th><th>Cost</th></tr></thead>
            <tbody>
              {activations.map((a) => (
                <tr key={a.id} data-testid={`activation-${a.id}`}>
                  <td className="text-neutral-400">{new Date(a.created_at).toLocaleString()}</td>
                  <td>
                    <div className="tabular">{a.user_no}</div>
                    <div className="text-xs text-neutral-500">{a.user_name}</div>
                  </td>
                  <td>{a.package_name} <span className="text-neutral-500">(${a.package_value})</span></td>
                  <td className="tabular">${a.cost.toFixed(2)}</td>
                </tr>
              ))}
              {activations.length === 0 && <tr><td colSpan={4} className="text-center text-neutral-500 py-6">No activation requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "deposits" && (
        <div className="lt-card p-6 overflow-x-auto" data-testid="deposits-table">
          <div className="overline mb-3">Fund requests (users who paid)</div>
          <table className="lt-table min-w-[760px]">
            <thead><tr><th>When</th><th>User</th><th>Amount</th><th>Note / Tx</th><th>Proof</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {fundRequests.map((f) => (
                <tr key={f.id} data-testid={`fund-request-${f.id}`}>
                  <td className="text-neutral-400">{new Date(f.created_at).toLocaleString()}</td>
                  <td>
                    <div className="tabular">{f.user_no}</div>
                    <div className="text-xs text-neutral-500">{f.full_name}</div>
                  </td>
                  <td className="tabular gold-text">${Number(f.amount).toFixed(2)}</td>
                  <td className="text-neutral-400 break-all max-w-[320px]" title={f.note}>{f.note || "—"}</td>
                  <td>
                    {f.proof_path ? (
                      <img
                        src={`${API}/files/${f.proof_path}`}
                        alt="payment proof"
                        className="h-14 w-14 object-cover rounded-md border border-white/10 cursor-pointer hover:opacity-80"
                        onClick={() => setPreviewImg(`${API}/files/${f.proof_path}`)}
                        data-testid={`fund-proof-thumb-${f.id}`}
                      />
                    ) : "—"}
                  </td>
                  <td>
                    <span className={`uppercase tracking-widest text-xs ${statusColor(f.status)}`}>{f.status}</span>
                  </td>
                  <td>
                    {f.status === "pending" && (
                      <div className="flex gap-3">
                        <button onClick={() => actFund(f.id, "approve")} className="text-xs gold-text hover:underline" data-testid={`approve-fund-${f.id}`}>Approve</button>
                        <button onClick={() => actFund(f.id, "reject")} className="text-xs text-red-400 hover:underline" data-testid={`reject-fund-${f.id}`}>Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {fundRequests.length === 0 && <tr><td colSpan={7} className="text-center text-neutral-500 py-6">No fund requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {previewImg && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setPreviewImg(null)}
          data-testid="fund-proof-modal"
        >
          <img src={previewImg} alt="payment proof" className="max-h-[85vh] max-w-[90vw] rounded-lg" />
        </div>
      )}

      {tab === "bep20" && (
        <div className="lt-card p-6" data-testid="bep20-admin-table">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="overline">BEP20 Addresses of Users</div>
            <input
              className="lt-input max-w-xs"
              placeholder="Search by ID or name…"
              value={bep20Search}
              onChange={(e) => setBep20Search(e.target.value)}
              data-testid="bep20-search"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="lt-table min-w-[760px]">
              <thead><tr><th>User ID</th><th>Name</th><th>BEP20 Address</th><th>Action</th></tr></thead>
              <tbody>
                {bep20Rows
                  .filter((r) => {
                    const q = bep20Search.trim().toLowerCase();
                    if (!q) return true;
                    return String(r.user_no).includes(q) || (r.full_name || "").toLowerCase().includes(q);
                  })
                  .slice(0, 300)
                  .map((r) => {
                    const edited = bep20Edits[r.user_no] ?? r.bep20_address;
                    return (
                      <tr key={r.user_no} data-testid={`bep20-row-${r.user_no}`}>
                        <td className="tabular">{r.user_no}</td>
                        <td className="text-neutral-300">{r.full_name || "—"}</td>
                        <td>
                          <input
                            className="lt-input tabular w-full min-w-[320px] text-xs"
                            value={edited}
                            placeholder="0x… (not set)"
                            onChange={(e) => setBep20Edits((p) => ({ ...p, [r.user_no]: e.target.value }))}
                            data-testid={`bep20-input-${r.user_no}`}
                          />
                        </td>
                        <td>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => copyBep20(r.user_no, r.bep20_address)}
                              disabled={!r.bep20_address}
                              className="text-xs text-neutral-300 hover:gold-text disabled:opacity-30"
                              data-testid={`bep20-copy-${r.user_no}`}
                            >
                              {bep20Copied === r.user_no ? "Copied!" : "Copy"}
                            </button>
                            <button
                              onClick={() => saveBep20(r.user_no)}
                              disabled={(bep20Edits[r.user_no] ?? r.bep20_address) === r.bep20_address}
                              className="text-xs gold-text hover:underline disabled:opacity-30 disabled:no-underline"
                              data-testid={`bep20-save-${r.user_no}`}
                            >
                              Save
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="lt-card p-6 overflow-x-auto">
          <ExportUsersButton />
          <table className="lt-table min-w-[900px]">
            <thead><tr><th>User ID</th><th>Name</th><th>Mobile</th><th>Email</th><th>Sponsor</th><th>Package</th><th>Directs</th><th>Rank</th><th>Global Club</th><th>Main</th><th>Deposit</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="tabular">{u.user_id}</td>
                  <td>{u.full_name}</td>
                  <td className="tabular text-neutral-300" data-testid={`user-mobile-${u.user_id}`}>{u.mobile || "—"}</td>
                  <td className="text-neutral-400">{u.email}</td>
                  <td className="tabular">{u.sponsor_user_id || "—"}</td>
                  <td>{u.package || <span className="text-neutral-500">—</span>}</td>
                  <td className="tabular">{u.direct_referral_count || 0}</td>
                  <td>{u.current_rank || "—"}</td>
                  <td className="tabular">{u.star_club || 0}</td>
                  <td className="tabular gold-text">${(u.wallet?.main || 0).toFixed(2)}</td>
                  <td className="tabular">${(u.wallet?.deposit || 0).toFixed(2)}</td>
                  <td>
                    <div className="flex gap-3 flex-wrap">
                      <button
                        onClick={() => openReset(u)}
                        className="text-xs gold-text hover:underline"
                        data-testid={`reset-pw-${u.user_id}`}
                      >
                        Reset password
                      </button>
                      <button
                        onClick={() => { setScLookup(u.user_id); setTab("starclub"); loadStarClub(u.user_id); }}
                        className="text-xs gold-text hover:underline"
                        data-testid={`view-starclub-${u.user_id}`}
                      >
                        Global Club
                      </button>
                      <button
                        onClick={() => { setDcLookup(u.user_id); setTab("donation"); loadDonationClub(u.user_id); }}
                        className="text-xs gold-text hover:underline"
                        data-testid={`view-donation-${u.user_id}`}
                      >
                        Donation
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "credit" && (
        <div className="space-y-6">
        <form onSubmit={doTopup} className="lt-card p-6 max-w-xl space-y-4" data-testid="admin-topup-form">
          <div>
            <div className="overline">Add fund to Admin Fund</div>
            <div className="text-xs text-neutral-500 mt-1">Top up your own Main wallet so you have funds to send. Current pool: <span className="gold-text tabular">${(stats?.platform_fee || 0).toFixed(2)}</span></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="overline block mb-2">Amount (USD)</label>
              <input type="number" step="0.01" min="0" className="lt-input tabular" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} required data-testid="admin-topup-amount" />
            </div>
            <button className="lt-btn-primary" data-testid="admin-topup-submit">Add to Admin Fund</button>
          </div>
        </form>

        <form onSubmit={doCredit} className="lt-card p-6 max-w-xl space-y-4">
          <div>
            <div className="overline">Send funds from Admin Fund</div>
            <div className="text-xs text-neutral-500 mt-1">Available: <span className="gold-text tabular">${(stats?.platform_fee || 0).toFixed(2)}</span></div>
          </div>
          <div>
            <label className="overline block mb-2">User ID</label>
            <input className="lt-input tabular" value={credit.user_id} onChange={(e) => setCredit({ ...credit, user_id: e.target.value })} required data-testid="admin-credit-userid" />
            {creditUserName && (
              <div className="text-xs mt-1.5" data-testid="admin-credit-username">
                {creditUserName === "__not_found__"
                  ? <span className="text-red-400">No member found with this ID</span>
                  : <span className="gold-text">Name: {creditUserName}</span>}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Amount (USD)</label>
              <input type="number" step="0.01" min="0" className="lt-input tabular" value={credit.amount} onChange={(e) => setCredit({ ...credit, amount: e.target.value })} required data-testid="admin-credit-amount" />
            </div>
            <div>
              <label className="overline block mb-2">Recipient wallet</label>
              <select className="lt-input" value={credit.wallet} onChange={(e) => setCredit({ ...credit, wallet: e.target.value })} data-testid="admin-credit-wallet">
                <option value="deposit">Deposit (for purchases)</option>
                <option value="main">Main (withdrawable)</option>
              </select>
            </div>
          </div>
          {msg.text && <div className={`text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`}>{msg.text}</div>}
          <button className="lt-btn-primary" data-testid="admin-credit-submit">Send funds</button>
        </form>

        <div className="lt-card p-6 overflow-x-auto" data-testid="fund-transfers-table">
          <div className="overline mb-3">Fund transfer history</div>
          {transfers.length === 0 ? (
            <div className="text-sm text-neutral-500">No fund transfers yet.</div>
          ) : (
            <table className="lt-table min-w-[760px]">
              <thead><tr><th>When</th><th>User</th><th>Wallet</th><th>Amount</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.id} data-testid={`transfer-row-${t.id}`}>
                    <td className="text-neutral-400 text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : "—"}</td>
                    <td className="tabular">{t.user_no}</td>
                    <td className="capitalize">{t.wallet}</td>
                    <td className="tabular gold-text">${Number(t.amount).toFixed(2)}</td>
                    <td>
                      {t.reversed
                        ? <span className="text-xs text-red-400">Reversed</span>
                        : <span className="text-xs text-emerald-400">Completed</span>}
                    </td>
                    <td>
                      {t.reversed ? (
                        <span className="text-xs text-neutral-600">—</span>
                      ) : (
                        <button onClick={() => reverseTransfer(t.id)} className="text-xs text-red-400 hover:underline" data-testid={`reverse-transfer-${t.id}`}>Reverse</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </div>
      )}

      {tab === "withdrawals" && (
        <div className="lt-card p-6 overflow-x-auto">
          <table className="lt-table">
            <thead><tr><th>When</th><th>User</th><th>Amount</th><th>Net</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.id}>
                  <td className="text-neutral-400">{new Date(w.created_at).toLocaleString()}</td>
                  <td className="tabular">{w.user_no}</td>
                  <td className="tabular">${w.amount.toFixed(2)}</td>
                  <td className="tabular gold-text">${w.net.toFixed(2)}</td>
                  <td><span className={`uppercase tracking-widest text-xs ${statusColor(w.status)}`}>{w.status}</span></td>
                  <td>
                    {w.status === "pending" && (
                      <div className="flex gap-2">
                        <button onClick={() => act(w.id, "approve")} className="text-xs gold-text" data-testid={`approve-${w.id}`}>Approve</button>
                        <button onClick={() => act(w.id, "reject")} className="text-xs text-red-400" data-testid={`reject-${w.id}`}>Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {withdrawals.length === 0 && <tr><td colSpan={6} className="text-center text-neutral-500 py-6">No withdrawal requests.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "starclub" && (
        <div className="space-y-5" data-testid="admin-starclub-panel">
          <form onSubmit={loadStarClub} className="lt-card p-6 max-w-xl">
            <div className="overline mb-3">Lookup user&apos;s Global Club position</div>
            <div className="flex gap-2">
              <input
                className="lt-input tabular"
                placeholder="User ID (e.g. 100109)"
                value={scLookup}
                onChange={(e) => setScLookup(e.target.value)}
                data-testid="admin-sc-lookup-input"
              />
              <button type="submit" className="lt-btn-primary whitespace-nowrap" disabled={scLoading} data-testid="admin-sc-lookup-btn">
                {scLoading ? "Loading…" : "View"}
              </button>
            </div>
            {scMsg.text && (
              <div className={`text-sm mt-3 ${scMsg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="admin-sc-msg">
                {scMsg.text}
              </div>
            )}
            <p className="text-xs text-neutral-500 mt-3">
              Shows which 5 users are filling slots below this user in each active club — their effective Global Club downline.
            </p>
          </form>

          {scData && (
            <div className="space-y-4">
              <div className="lt-card p-6">
                <div className="overline">User</div>
                <div className="mt-2 grid md:grid-cols-6 gap-4">
                  <div>
                    <div className="text-xs text-neutral-500">ID</div>
                    <div className="font-display text-2xl tabular" data-testid="admin-sc-user-id">{scData.user.user_id}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Name</div>
                    <div className="text-base">{scData.user.full_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Package</div>
                    <div className="text-base tabular">${scData.user.package_value || 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Current Club</div>
                    <div className="text-base gold-text tabular">Club {scData.user.current_club}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Directs</div>
                    <div className="text-base tabular" data-testid="admin-sc-direct-count">{scData.user.direct_count}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Total team</div>
                    <div className="text-base tabular" data-testid="admin-sc-total-team">{scData.user.total_team} <span className="text-neutral-500 text-xs">({scData.user.total_active_team} active)</span></div>
                  </div>
                </div>
              </div>

              <div className="lt-card p-6" data-testid="admin-sc-sponsor-downline">
                <div className="overline mb-3">Sponsor Downline — Direct + Indirect (all 11 levels)</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
                  {scData.level_layers.map((l) => (
                    <div key={l.level} className="lt-card p-3" data-testid={`admin-sc-level-${l.level}`}>
                      <div className="overline">Level {l.level}</div>
                      <div className="font-display text-xl tabular mt-1">{l.count}</div>
                      <div className="text-[10px] text-neutral-500 mt-0.5">{l.active_count} active</div>
                    </div>
                  ))}
                </div>

                <div className="overline mb-2">Direct downline ({scData.directs.length})</div>
                {scData.directs.length === 0 ? (
                  <div className="text-sm text-neutral-500 py-3" data-testid="admin-sc-no-directs">No direct referrals.</div>
                ) : (
                  <table className="lt-table">
                    <thead>
                      <tr>
                        <th>User ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Package</th>
                        <th>Global Club</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scData.directs.map((d) => (
                        <tr key={d.user_id} data-testid={`admin-sc-direct-${d.user_id}`}>
                          <td className="tabular">
                            <button
                              type="button"
                              className="gold-text hover:underline"
                              onClick={() => { setScLookup(d.user_id); loadStarClub(d.user_id); }}
                              data-testid={`admin-sc-drilldown-${d.user_id}`}
                            >
                              {d.user_id}
                            </button>
                          </td>
                          <td>{d.full_name}</td>
                          <td className="text-neutral-400 text-xs">{d.email}</td>
                          <td className="tabular">{d.package ? `${d.package} ($${d.package_value})` : <span className="text-neutral-500">—</span>}</td>
                          <td className="tabular">{d.star_club || 0} / 9</td>
                          <td className="text-neutral-400 text-xs">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="text-[10px] text-neutral-500 mt-3">
                  Click any User ID above to drill down into their Global Club + downline.
                </div>
              </div>

              {scData.waiting.length === 0 && (
                <div className="lt-card p-6 text-sm text-neutral-400" data-testid="admin-sc-no-waiting">
                  No active Global Club queue entries for this user.
                </div>
              )}

              {scData.waiting.map((entry) => (
                <div key={entry.id} className="lt-card p-6" data-testid={`admin-sc-club-${entry.club}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-3">
                    <div>
                      <div className="overline">Club {entry.club} — Waiting</div>
                      <div className="mt-1 text-sm text-neutral-400">
                        Position #{entry.position + 1} in global queue • Slots filled: <span className="gold-text tabular">{entry.slots_filled} / 5</span>
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500 tabular">
                      Entry ${scData.clubs[entry.club - 1]?.entry} • Pool ${scData.clubs[entry.club - 1]?.pool}
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="overline mb-2">Global Club downline (next 5 in same club queue)</div>
                    <table className="lt-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Joined club</th>
                          <th>Their slots</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.downstream && entry.downstream.length > 0 ? (
                          entry.downstream.map((d, idx) => (
                            <tr key={`${entry.id}-${d.position}`} data-testid={`admin-sc-downstream-${entry.club}-${idx}`}>
                              <td className="tabular text-neutral-500">{idx + 1}</td>
                              <td className="tabular">{d.user_no}</td>
                              <td>{d.full_name || "—"}</td>
                              <td className="text-neutral-400 text-xs">{d.email || "—"}</td>
                              <td className="text-neutral-400 text-xs">{new Date(d.created_at).toLocaleString()}</td>
                              <td className="tabular">{d.slots_filled} / 5</td>
                              <td className="text-xs uppercase tracking-widest">{d.status}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={7} className="text-center text-neutral-500 py-4">No one is below this user in Club {entry.club} yet.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {scData.completed.length > 0 && (
                <div className="lt-card p-6">
                  <div className="overline mb-3">Completed Clubs</div>
                  <div className="flex flex-wrap gap-2">
                    {scData.completed.map((c) => (
                      <div key={c.id} className="px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-xs gold-text tabular">
                        Club {c.club}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scData.history.length > 0 && (
                <div className="lt-card p-6">
                  <div className="overline mb-3">Payout History</div>
                  <table className="lt-table">
                    <thead><tr><th>When</th><th>Club</th><th>Payout</th><th>Rebirth</th></tr></thead>
                    <tbody>
                      {scData.history.map((h) => (
                        <tr key={h.id}>
                          <td className="text-neutral-400">{new Date(h.created_at).toLocaleString()}</td>
                          <td>Club {h.club}</td>
                          <td className="tabular gold-text">+${h.payout}</td>
                          <td className="tabular">+${h.rebirth}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "achievers" && (
        <div className="space-y-5" data-testid="admin-achievers-panel">
          <div className="lt-card p-6">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="overline">Global Club Achievers</div>
                <h2 className="font-display text-3xl mt-1">Members who completed 5 below</h2>
                <p className="text-sm text-neutral-400 mt-2">
                  A member becomes an Achiever the moment they fill their 5-slot matrix in any Global Club. List ordered by total clubs completed.
                </p>
              </div>
              <div>
                <label className="overline block mb-2">Filter by club</label>
                <select
                  className="lt-input tabular"
                  value={achClub}
                  onChange={(e) => setAchClub(e.target.value)}
                  data-testid="achievers-club-filter"
                >
                  <option value="">All clubs</option>
                  {[1,2,3,4,5,6,7,8,9].map((n) => (
                    <option key={n} value={n}>Club {n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="text-sm text-neutral-400 mb-3">
              {achLoading ? "Loading achievers…" : (
                <>Total: <span className="gold-text tabular">{achievers.length}</span> achiever{achievers.length === 1 ? "" : "s"}{achClub ? ` in Club ${achClub}` : ""}</>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="lt-table min-w-[900px]">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Package</th>
                    <th>Current Club</th>
                    <th>Clubs Completed</th>
                    <th>Total Payout</th>
                    <th>Last Achieved</th>
                  </tr>
                </thead>
                <tbody>
                  {achievers.map((a, idx) => (
                    <tr key={a.user_id} data-testid={`achiever-${a.user_id}`}>
                      <td className="tabular text-neutral-500">{idx + 1}</td>
                      <td className="tabular">
                        <button
                          type="button"
                          className="gold-text hover:underline"
                          onClick={() => { setScLookup(a.user_id); setTab("starclub"); loadStarClub(a.user_id); }}
                          data-testid={`achiever-drilldown-${a.user_id}`}
                        >
                          {a.user_id}
                        </button>
                      </td>
                      <td>{a.full_name}</td>
                      <td className="text-neutral-400 text-xs">{a.email}</td>
                      <td className="tabular">{a.package ? `${a.package} ($${a.package_value})` : <span className="text-neutral-500">—</span>}</td>
                      <td className="tabular">Club {a.current_club}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {a.clubs_completed.map((c) => (
                            <span key={c} className="px-2 py-0.5 text-[10px] rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 gold-text tabular">
                              C{c}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="tabular gold-text">${a.total_payout.toFixed(2)}</td>
                      <td className="text-neutral-400 text-xs">{a.last_completed_at ? new Date(a.last_completed_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                  {!achLoading && achievers.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-neutral-500 py-6">No achievers yet{achClub ? ` in Club ${achClub}` : ""}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "donation" && (
        <div className="space-y-5" data-testid="admin-donation-panel">
          <form onSubmit={loadDonationClub} className="lt-card p-6 max-w-xl">
            <div className="overline mb-3">Lookup user&apos;s Donation Club</div>
            <div className="flex gap-2">
              <input
                className="lt-input tabular"
                placeholder="User ID (e.g. 100109)"
                value={dcLookup}
                onChange={(e) => setDcLookup(e.target.value)}
                data-testid="admin-dc-lookup-input"
              />
              <button type="submit" className="lt-btn-primary whitespace-nowrap" disabled={dcLoading} data-testid="admin-dc-lookup-btn">
                {dcLoading ? "Loading…" : "View"}
              </button>
            </div>
            {dcMsg.text && (
              <div className={`text-sm mt-3 ${dcMsg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="admin-dc-msg">
                {dcMsg.text}
              </div>
            )}
            <p className="text-xs text-neutral-500 mt-3">
              Shows the user&apos;s upline &amp; downline (team members) plus all donation income received/contributed.
            </p>
          </form>

          {dcData && <DonationClubView data={dcData} />}
        </div>
      )}

      {resetTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
          onClick={closeReset}
          data-testid="reset-password-modal"
        >
          <form
            onSubmit={submitReset}
            onClick={(e) => e.stopPropagation()}
            className="lt-card p-6 w-full max-w-md space-y-4"
          >
            <div>
              <div className="overline">Reset password</div>
              <div className="font-display text-2xl mt-1">{resetTarget.full_name}</div>
              <div className="text-xs text-neutral-500 mt-1 tabular">ID {resetTarget.user_id}</div>
            </div>
            <div>
              <label className="overline block mb-2">New login password</label>
              <input
                type="password" minLength={6} required
                className="lt-input"
                value={resetForm.new_password}
                onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })}
                data-testid="reset-new-password"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-400">
              <input
                type="checkbox"
                checked={resetForm.reset_tpass}
                onChange={(e) => setResetForm({ ...resetForm, reset_tpass: e.target.checked })}
                data-testid="reset-tpass-checkbox"
              />
              Also reset transaction password
            </label>
            {resetForm.reset_tpass && (
              <div>
                <label className="overline block mb-2">New transaction password</label>
                <input
                  type="password" minLength={4} required
                  className="lt-input"
                  value={resetForm.new_t_pass}
                  onChange={(e) => setResetForm({ ...resetForm, new_t_pass: e.target.value })}
                  data-testid="reset-new-tpass"
                />
              </div>
            )}
            {resetMsg.text && (
              <div className={`text-sm ${resetMsg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="reset-msg">
                {resetMsg.text}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={closeReset} className="lt-btn-secondary" data-testid="reset-cancel">Cancel</button>
              <button type="submit" className="lt-btn-primary" data-testid="reset-submit">Reset password</button>
            </div>
          </form>
        </div>
      )}

      {tab === "playbox" && (
        <PlayBoxTab />
      )}

      {tab === "usertxns" && (
        <UserTransactionsTab />
      )}

      {tab === "system" && (
        <SystemTab lpHistory={lpHistory} reload={load} />
      )}

      {fundWd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="fund-withdraw-modal" onClick={() => !fundWdBusy && setFundWd(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitFundWd} className="lt-card p-6 w-full max-w-md space-y-4 border border-amber-500/30">
            <div className="flex items-center justify-between">
              <div className="font-display text-xl">Withdraw from {fundWd.label}</div>
              <button type="button" onClick={() => setFundWd(null)} className="text-neutral-400 hover:text-white" data-testid="fund-withdraw-close">✕</button>
            </div>
            <div className="text-sm text-neutral-400">
              Manually reduce the {fundWd.label} balance (for funds used or withdrawn externally). This is logged.
            </div>
            <div>
              <div className="overline">Amount (USD)</div>
              <input type="number" step="0.01" min="0.01" className="lt-input tabular mt-1" value={fundWdForm.amount}
                onChange={(e) => setFundWdForm({ ...fundWdForm, amount: e.target.value })} required data-testid="fund-withdraw-amount" />
            </div>
            <div>
              <div className="overline">Reason (optional)</div>
              <input className="lt-input mt-1" value={fundWdForm.reason}
                onChange={(e) => setFundWdForm({ ...fundWdForm, reason: e.target.value })} data-testid="fund-withdraw-reason" />
            </div>
            <div>
              <div className="overline">Transaction Password</div>
              <input type="password" className="lt-input mt-1" value={fundWdForm.t_pass}
                onChange={(e) => setFundWdForm({ ...fundWdForm, t_pass: e.target.value })} required data-testid="fund-withdraw-tpass" />
            </div>
            {fundWdMsg && <div className={`text-sm ${fundWdMsg.startsWith("✓") ? "text-emerald-400" : "text-red-400"}`} data-testid="fund-withdraw-msg">{fundWdMsg}</div>}
            <button type="submit" disabled={fundWdBusy} className="lt-btn-primary w-full" data-testid="fund-withdraw-submit">
              {fundWdBusy ? "Processing…" : `Withdraw from ${fundWd.label}`}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

const WALLET_LABELS = { main: "Main", deposit: "Deposit", lp: "NFT Card Fund", rebirth: "Rebirth" };

function UserTransactionsTab() {
  const [query, setQuery] = React.useState("");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState({ type: "", text: "" });
  const [adj, setAdj] = React.useState({ wallet: "main", direction: "deduct", amount: "", reason: "", t_pass: "" });
  const [busy, setBusy] = React.useState(false);
  const [migrate, setMigrate] = React.useState(null);

  const runMigrate = async (apply) => {
    if (apply && !window.confirm("Move all past admin deductions out of the LP Pool and into the Admin Fund? This runs once and cannot be undone.")) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post(`/admin/migrate-deductions-to-admin?apply=${apply ? "true" : "false"}`);
      setMigrate(r.data);
      if (apply) setMsg({ type: "ok", text: r.data.note });
    } catch (err) {
      setMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally { setBusy(false); }
  };

  const lookup = async (e) => {
    if (e) e.preventDefault();
    const uno = query.trim();
    if (!uno) return;
    setLoading(true); setMsg({ type: "", text: "" }); setData(null);
    try {
      const r = await api.get(`/admin/user-transactions/${encodeURIComponent(uno)}`);
      setData(r.data);
    } catch (err) {
      setMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally { setLoading(false); }
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    if (!data) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/user-adjust", {
        user_id: data.user.user_id,
        wallet: adj.wallet,
        direction: adj.direction,
        amount: Number(adj.amount),
        reason: adj.reason || null,
        t_pass: adj.t_pass,
      });
      setMsg({ type: "ok", text: `${r.data.direction === "add" ? "Added" : "Deducted"} $${r.data.amount.toFixed(2)} ${r.data.direction === "add" ? "to" : "from"} ${WALLET_LABELS[r.data.wallet]}. New balance: $${r.data.new_balance.toFixed(2)}.` });
      setAdj({ ...adj, amount: "", reason: "", t_pass: "" });
      await lookup();
    } catch (err) {
      setMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-usertxns">
      <form onSubmit={lookup} className="lt-card p-6 flex flex-wrap items-end gap-3" data-testid="usertxns-search-form">
        <div className="flex-1 min-w-[220px]">
          <div className="overline">User ID</div>
          <input className="lt-input tabular mt-1" placeholder="e.g. 100002" value={query}
            onChange={(e) => setQuery(e.target.value)} required data-testid="usertxns-search-input" />
        </div>
        <button type="submit" disabled={loading} className="lt-btn-primary" data-testid="usertxns-search-btn">
          {loading ? "Loading…" : "View transactions"}
        </button>
      </form>

      {msg.text && (
        <div className={`text-sm ${msg.type === "ok" ? "text-emerald-400" : "text-red-400"}`} data-testid="usertxns-msg">{msg.text}</div>
      )}

      <div className="lt-card p-5 border border-amber-500/20" data-testid="usertxns-migrate-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="overline text-amber-300">Deduction destination</div>
            <div className="text-sm text-neutral-400 mt-1">Deducted funds now go to the Admin Fund. Earlier deductions that went to the LP Pool can be moved over here once.</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => runMigrate(false)} disabled={busy} className="lt-btn-secondary text-sm" data-testid="migrate-preview-btn">
              {busy ? "…" : "Preview"}
            </button>
            {migrate && migrate.total_moved_to_admin > 0 && !migrate.applied && (
              <button onClick={() => runMigrate(true)} disabled={busy} className="lt-btn-primary text-sm" data-testid="migrate-apply-btn">
                Move ${migrate.total_moved_to_admin} to Admin Fund
              </button>
            )}
          </div>
        </div>
        {migrate && (
          <div className="text-xs text-neutral-400 mt-3" data-testid="migrate-result">
            {migrate.deductions_found} past deduction(s) · ${migrate.total_moved_to_admin} · {migrate.note}
          </div>
        )}
      </div>

      {data && (
        <>
          <div className="lt-card p-6" data-testid="usertxns-user-card">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <div className="font-display text-2xl">{data.user.full_name}</div>
                <div className="text-sm text-neutral-400">ID {data.user.user_id} · {data.user.email} · {data.user.mobile}</div>
              </div>
              <div className="text-xs text-neutral-500">{data.count} transactions</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
              {["main", "deposit", "lp"].map((k) => (
                <div key={k}>
                  <div className="overline">{WALLET_LABELS[k]}</div>
                  <div className="font-display text-2xl tabular mt-1 gold-text" data-testid={`usertxns-bal-${k}`}>${data.wallets[k].toFixed(2)}</div>
                </div>
              ))}
              <div>
                <div className="overline">Play Principal</div>
                <div className="font-display text-2xl tabular mt-1 gold-text" data-testid="usertxns-play-principal">${(data.play_principal || 0).toFixed(2)}</div>
                <div className="text-[10px] text-neutral-500 mt-1">Locked in open Play Card boxes</div>
              </div>
            </div>
          </div>

          <form onSubmit={submitAdjust} className="lt-card p-6 space-y-4 border border-amber-500/30" data-testid="usertxns-adjust-form">
            <div className="overline text-amber-300">Adjust funds</div>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <div className="overline">Action</div>
                <select className="lt-input mt-1" value={adj.direction} onChange={(e) => setAdj({ ...adj, direction: e.target.value })} data-testid="usertxns-direction">
                  <option value="deduct">Deduct</option>
                  <option value="add">Add</option>
                </select>
              </div>
              <div>
                <div className="overline">Wallet</div>
                <select className="lt-input mt-1" value={adj.wallet} onChange={(e) => setAdj({ ...adj, wallet: e.target.value })} data-testid="usertxns-wallet">
                  {["main", "deposit", "lp", "rebirth"].map((k) => <option key={k} value={k}>{WALLET_LABELS[k]}</option>)}
                </select>
              </div>
              <div>
                <div className="overline">Amount (USD)</div>
                <input type="number" step="0.01" min="0.01" className="lt-input tabular mt-1" value={adj.amount}
                  onChange={(e) => setAdj({ ...adj, amount: e.target.value })} required data-testid="usertxns-amount" />
              </div>
              <div>
                <div className="overline">Transaction Password</div>
                <input type="password" className="lt-input mt-1" value={adj.t_pass}
                  onChange={(e) => setAdj({ ...adj, t_pass: e.target.value })} required data-testid="usertxns-tpass" />
              </div>
            </div>
            <div>
              <div className="overline">Reason (optional)</div>
              <input className="lt-input mt-1" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
                placeholder="e.g. correction, penalty…" data-testid="usertxns-reason" />
            </div>
            <div className="text-[11px] text-neutral-500">Deductions move the amount into the system LP Pool. This action is logged.</div>
            <button type="submit" disabled={busy} className="lt-btn-primary" data-testid="usertxns-adjust-btn">
              {busy ? "Processing…" : (adj.direction === "add" ? "Add funds" : "Deduct funds")}
            </button>
          </form>

          <div className="lt-card p-6" data-testid="usertxns-ledger">
            <div className="overline mb-3">Transaction history</div>
            {data.transactions.length === 0 ? (
              <div className="text-sm text-neutral-400">No transactions found.</div>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="lt-table">
                  <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Wallet</th><th>Status</th><th className="text-right">Amount</th></tr></thead>
                  <tbody>
                    {data.transactions.map((t, i) => (
                      <tr key={`${t.date}-${t.kind}-${t.amount}-${i}`} data-testid={`usertxns-row-${i}`}>
                        <td className="tabular text-neutral-400 whitespace-nowrap">{(t.date || "").slice(0, 19).replace("T", " ")}</td>
                        <td className="capitalize">{t.kind.replace(/_/g, " ")}</td>
                        <td>{t.label}{t.note ? <span className="text-neutral-500"> · {t.note}</span> : null}</td>
                        <td className="uppercase text-xs text-neutral-400">{t.wallet ? (WALLET_LABELS[t.wallet] || t.wallet) : "—"}</td>
                        <td className={`text-xs ${statusColor(t.status)}`}>{t.status || "—"}</td>
                        <td className={`tabular text-right whitespace-nowrap ${t.direction === "credit" ? "text-emerald-400" : t.direction === "debit" ? "text-red-400" : "text-neutral-400"}`}>
                          {t.direction === "credit" ? "+" : t.direction === "debit" ? "−" : ""}${Math.abs(t.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PlayBoxTab() {
  const [data, setData] = React.useState({ payouts: [], refund_events: [] });
  const [filter, setFilter] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/admin/play-card/payouts");
        if (mounted) { setData(r.data); setLoading(false); }
      } catch (e) {
        console.warn("Play box payouts load:", e?.message);
        setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const filtered = data.payouts.filter((p) =>
    !filter ||
    String(p.user_no || "").includes(filter) ||
    String(p.full_name || "").toLowerCase().includes(filter.toLowerCase()) ||
    String(p.kind || "").toLowerCase().includes(filter.toLowerCase())
  );

  const totals = filtered.reduce((acc, p) => {
    acc.total += Number(p.amount || 0);
    if (p.kind === "Investor 10%") acc.investor += Number(p.amount || 0);
    else if (p.kind.startsWith("Level")) acc.level += Number(p.amount || 0);
    return acc;
  }, { total: 0, investor: 0, level: 0 });

  return (
    <div className="space-y-6" data-testid="admin-playbox-tab">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="lt-card p-5">
          <div className="overline">Total Play Card payouts</div>
          <div className="font-display text-3xl tabular mt-2 gold-text" data-testid="pb-total">${totals.total.toFixed(3)}</div>
        </div>
        <div className="lt-card p-5">
          <div className="overline">Investor 10%</div>
          <div className="font-display text-3xl tabular mt-2" data-testid="pb-investor">${totals.investor.toFixed(3)}</div>
        </div>
        <div className="lt-card p-5">
          <div className="overline">Level commissions</div>
          <div className="font-display text-3xl tabular mt-2" data-testid="pb-level">${totals.level.toFixed(3)}</div>
        </div>
      </div>

      <div className="lt-card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Play Box distribution &amp; refund history</div>
            <div className="text-xs text-neutral-500 mt-1">Auto-refreshes every 8 s · showing {filtered.length} of {data.payouts.length} payouts</div>
          </div>
          <input
            type="text" placeholder="Filter by user ID / name / kind"
            className="lt-input text-sm w-72"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="pb-filter-input"
          />
        </div>
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0F0F12]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/5">
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Distributed to</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Kind</th>
                <th className="px-4 py-2">Amount</th>
                <th className="px-4 py-2">Ref</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="px-4 py-6 text-center text-neutral-500">Loading…</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-neutral-500">No Play Card payouts yet.</td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-white/[0.03]" data-testid={`pb-row-${p.id}`}>
                  <td className="px-4 py-2 text-neutral-400 text-xs">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2 tabular">{p.user_no || "—"}</td>
                  <td className="px-4 py-2">{p.full_name || "—"}</td>
                  <td className={`px-4 py-2 ${p.kind === "Investor 10%" ? "gold-text" : ""}`}>{p.kind}</td>
                  <td className="px-4 py-2 tabular">${Number(p.amount || 0).toFixed(3)}</td>
                  <td className="px-4 py-2 text-neutral-500 text-xs">{p.ref || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.refund_events && data.refund_events.length > 0 && (
        <div className="lt-card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5">
            <div className="overline">Box closure events (principals refunded to contributors&apos; LP wallets)</div>
            <div className="text-xs text-neutral-500 mt-1">{data.refund_events.length} closures</div>
          </div>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0F0F12]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/5">
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Global drop #</th>
                  <th className="px-4 py-2">Box closed</th>
                  <th className="px-4 py-2">Triggered by user</th>
                </tr>
              </thead>
              <tbody>
                {data.refund_events.map((r, idx) => (
                  <tr key={`${r.global_drop_no}-${r.created_at || idx}`} className="border-b border-white/[0.03]">
                    <td className="px-4 py-2 text-neutral-400 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 tabular">{r.global_drop_no}</td>
                    <td className="px-4 py-2 gold-text tabular">Box {r.box_closed}</td>
                    <td className="px-4 py-2 tabular">{r.triggered_by_user_no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SystemTab({ lpHistory = [], reload }) {
  const [busy, setBusy] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const [confirmStale, setConfirmStale] = React.useState(false);
  const [confirmFactory, setConfirmFactory] = React.useState(false);
  const [msg, setMsg] = React.useState({ type: "", text: "" });
  const [cycleFix, setCycleFix] = React.useState(null); // dry-run preview result
  const [cdHours, setCdHours] = React.useState(3);
  const [cdMins, setCdMins] = React.useState(45);
  const [reconcile, setReconcile] = React.useState(null);
  const [auditNo, setAuditNo] = React.useState("");
  const [audit, setAudit] = React.useState(null);
  const [capSettle, setCapSettle] = React.useState(null);
  const [principalNo, setPrincipalNo] = React.useState("");
  const [lpDeduct, setLpDeduct] = React.useState({ amount: "", reason: "", t_pass: "" });
  const [lpPage, setLpPage] = React.useState(1);
  const LP_PER_PAGE = 10;
  const lpTotalPages = Math.max(1, Math.ceil(lpHistory.length / LP_PER_PAGE));
  const lpPageSafe = Math.min(lpPage, lpTotalPages);
  const lpPageRows = lpHistory.slice((lpPageSafe - 1) * LP_PER_PAGE, lpPageSafe * LP_PER_PAGE);

  const submitLpDeduct = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Deduct $${Number(lpDeduct.amount).toFixed(2)} from the LP Pool? This permanently lowers the pool balance.`)) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/lp-pool/deduct", {
        amount: Number(lpDeduct.amount),
        reason: lpDeduct.reason || null,
        t_pass: lpDeduct.t_pass,
      });
      setMsg({ type: "ok", text: `Deducted $${r.data.deducted.toFixed(2)} from LP Pool. New balance: $${r.data.lp_pool_balance.toFixed(2)}.` });
      setLpDeduct({ amount: "", reason: "", t_pass: "" });
      if (reload) await reload();
    } catch (err) {
      setMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally { setBusy(false); }
  };


  const runReconcile = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.get("/admin/play-card/reconcile");
      setReconcile(r.data);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const runAudit = async () => {
    if (!auditNo.trim()) return;
    setBusy(true); setMsg({ type: "", text: "" }); setAudit(null);
    try {
      const r = await api.get(`/admin/play-card/member-audit?user_no=${encodeURIComponent(auditNo.trim())}`);
      setAudit(r.data);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const releasePrincipal = async () => {
    if (!audit) return;
    const amt = audit.releasable_principal ?? audit.in_play_principal ?? 0;
    if (!window.confirm(`Release $${amt} of stuck in-play principal into ${audit.full_name}'s Game Fund so they can play? This clears their open box stakes and cannot be undone.`)) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/play-card/release-principal", { user_id: audit.user_no, apply: true });
      setMsg({ type: "ok", text: r.data.note || `Released $${r.data.stuck_principal} to Game Fund.` });
      await runAudit();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const transferPrincipalToGameFund = async () => {
    const uid = principalNo.trim();
    if (!uid) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      // Preview first to know the exact stuck/in-play principal amount.
      const preview = await api.post("/admin/play-card/release-principal", { user_id: uid, apply: false });
      const amt = Number(preview.data.stuck_principal || 0);
      if (amt <= 0) {
        setMsg({ type: "err", text: `${preview.data.full_name || uid} has no in-play principal to transfer.` });
        return;
      }
      if (!window.confirm(`Transfer $${amt.toFixed(2)} of ${preview.data.full_name || uid}'s in-play Play Card principal into their Game Fund? This clears their open box stakes and cannot be undone.`)) {
        return;
      }
      const r = await api.post("/admin/play-card/release-principal", { user_id: uid, apply: true });
      setMsg({ type: "ok", text: r.data.note || `Transferred $${r.data.stuck_principal} to Game Fund.` });
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const runSettleCaps = async (apply) => {
    if (apply && !window.confirm("Move the $5 principal from each capped member's Game Fund to the LP Pool and deactivate their game? This cannot be undone.")) return;
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post(`/admin/play-card/settle-caps?apply=${apply ? "true" : "false"}`);
      setCapSettle(r.data);
      if (apply) { setMsg({ type: "ok", text: `Settled ${r.data.members_affected} member(s); $${r.data.total_principal_to_lp} moved to LP Pool.` }); reload && reload(); }
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  React.useEffect(() => {
    api.get("/admin/play-card/cooldown").then((r) => {
      const s = r.data.seconds || 0;
      setCdHours(Math.floor(s / 3600));
      setCdMins(Math.floor((s % 3600) / 60));
    }).catch(() => {});
  }, []);

  const saveCooldown = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const seconds = (Number(cdHours) || 0) * 3600 + (Number(cdMins) || 0) * 60;
      const r = await api.post("/admin/play-card/set-cooldown", { seconds });
      const secs = r.data.cooldown_seconds || 0;
      const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
      setMsg({ type: "ok", text: `Cooldown set to ${h}h ${m}m and restarted — applies to everyone now.` });
    } catch (e) {
      setMsg({ type: "err", text: (e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const resetAll = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/play-card/reset-all");
      setMsg({ type: "ok", text: `Hard reset complete — ${r.data.play_cards_reset} users reset, ${r.data.history_rows_deleted} history rows cleared.` });
      setConfirm(false);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const factoryReset = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/factory-reset?confirm=WIPE_EVERYTHING");
      const d = r.data?.deleted || {};
      const summary = Object.entries(d)
        .filter(([, v]) => v && v > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join(" • ");
      setMsg({
        type: "ok",
        text: `Factory reset complete. ${summary || "All collections were already empty."} Admin seeded with $${r.data?.admin_seed_balance || 0}.`,
      });
      setConfirmFactory(false);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const previewCycleFix = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/migrate/reverse-cycle-bonuses?dry_run=true");
      setCycleFix(r.data);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const applyCycleFix = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/migrate/reverse-cycle-bonuses?dry_run=false");
      setMsg({
        type: "ok",
        text: `Refunded $${Number(r.data.total_member_refund).toFixed(2)} to ${r.data.members_refunded} member(s) and removed $${Number(r.data.lp_removed).toFixed(2)} from the LP pool.`,
      });
      setCycleFix(null);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const restartCooldown = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/play-card/restart-cooldown");
      const secs = r.data.cooldown_seconds || 0;
      const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
      setMsg({ type: "ok", text: `Cooldown restarted — next box opens in ${h}h ${m}m.` });
    } catch (e) {
      setMsg({ type: "err", text: (e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const resetStale = async () => {
    setBusy(true); setMsg({ type: "", text: "" });
    try {
      const r = await api.post("/admin/play-card/reset-stale");
      setMsg({
        type: "ok",
        text: `Stale reset done — ${r.data.users_with_claimable} users now have $${Number(r.data.total_parked || 0).toFixed(2)} total claimable principal. They will see a Claim button in Play Card.`,
      });
      setConfirmStale(false);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {/* Manual LP Pool deduction — reduce the pool for funds used/withdrawn externally */}
      <form onSubmit={submitLpDeduct} className="lt-card p-6 space-y-4 border border-red-500/25" data-testid="lp-deduct-card">
        <div>
          <div className="overline text-red-300">Deduct from LP Pool</div>
          <div className="text-sm text-neutral-400 mt-1">
            Manually reduce the LP / Liquidity Pool balance by any amount (for funds already used or
            withdrawn externally). This lowers the pool and is recorded in the LP history below.
            {lpHistory.length > 0 && (
              <> Current balance: <span className="gold-text tabular">${Number(lpHistory[0].balance_after).toFixed(2)}</span>.</>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <div className="overline">Amount (USD)</div>
            <input type="number" step="0.01" min="0.01" className="lt-input tabular mt-1" value={lpDeduct.amount}
              onChange={(e) => setLpDeduct({ ...lpDeduct, amount: e.target.value })} required data-testid="lp-deduct-amount" />
          </div>
          <div>
            <div className="overline">Reason (optional)</div>
            <input className="lt-input mt-1" value={lpDeduct.reason}
              onChange={(e) => setLpDeduct({ ...lpDeduct, reason: e.target.value })}
              placeholder="e.g. withdrawn to bank" data-testid="lp-deduct-reason" />
          </div>
          <div>
            <div className="overline">Transaction Password</div>
            <input type="password" className="lt-input mt-1" value={lpDeduct.t_pass}
              onChange={(e) => setLpDeduct({ ...lpDeduct, t_pass: e.target.value })} required data-testid="lp-deduct-tpass" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="lt-btn-primary" data-testid="lp-deduct-btn">
          {busy ? "Processing…" : "Deduct from LP Pool"}
        </button>
      </form>

      {/* LP Pool history (audit of every credit / debit) */}
      <div className="lt-card p-6 overflow-x-auto" data-testid="lp-history-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="overline">LP / Liquidity Pool history</div>
            <div className="text-xs text-neutral-500 mt-1">Every credit & debit to the LP pool, newest first.</div>
          </div>
          {lpHistory.length > 0 && (
            <div className="text-right">
              <div className="overline">Current balance</div>
              <div className="font-display text-xl tabular gold-text">${Number(lpHistory[0].balance_after).toFixed(2)}</div>
            </div>
          )}
        </div>
        {lpHistory.length === 0 ? (
          <div className="text-sm text-neutral-500" data-testid="lp-history-empty">No LP movements recorded yet.</div>
        ) : (
          <>
          <table className="lt-table min-w-[820px]">
            <thead><tr><th>When</th><th>User ID</th><th>Reason</th><th>Amount</th><th>Balance after</th></tr></thead>
            <tbody>
              {lpPageRows.map((r) => (
                <tr key={r.id} data-testid={`lp-history-row-${r.id}`}>
                  <td className="text-neutral-400 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                  <td className="tabular gold-text">{r.user_no || "—"}</td>
                  <td className="text-sm">{r.reason}</td>
                  <td className={`tabular ${Number(r.amount) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {Number(r.amount) >= 0 ? "+" : ""}${Number(r.amount).toFixed(2)}
                  </td>
                  <td className="tabular text-neutral-300">${Number(r.balance_after).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between gap-3 mt-4" data-testid="lp-history-pagination">
            <div className="text-xs text-neutral-500">
              Showing {(lpPageSafe - 1) * LP_PER_PAGE + 1}–{Math.min(lpPageSafe * LP_PER_PAGE, lpHistory.length)} of {lpHistory.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLpPage((p) => Math.max(1, p - 1))}
                disabled={lpPageSafe <= 1}
                className="lt-btn-secondary px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="lp-history-prev">
                Prev
              </button>
              <span className="text-sm tabular text-neutral-300" data-testid="lp-history-page-indicator">Page {lpPageSafe} / {lpTotalPages}</span>
              <button
                onClick={() => setLpPage((p) => Math.min(lpTotalPages, p + 1))}
                disabled={lpPageSafe >= lpTotalPages}
                className="lt-btn-secondary px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="lp-history-next">
                Next
              </button>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Refund / reverse past End-of-cycle $5 charges */}
      <div className="lt-card p-6 max-w-2xl space-y-4" data-testid="admin-cycle-bonus-fix-card">
        <div>
          <div className="overline">Accounting Correction</div>
          <h3 className="font-display text-2xl mt-1">Refund End-of-cycle $5 charges</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Undoes the End-of-cycle $5 mechanic entirely. Every member who was debited $5 for
            closing a cycle gets that money <strong>back into their Game Fund</strong>, and the
            matching $5 credits are removed from the <strong>LP pool</strong> so the books balance.
            A member&apos;s $5 now simply stays in their Game Fund until the 300% cap.
            It is <strong>idempotent</strong> — already-reversed records are skipped, so it&apos;s
            safe to run more than once.
          </p>
        </div>

        {cycleFix && (
          <div className="text-xs text-neutral-300 bg-white/[0.03] border border-white/10 rounded-lg p-3 space-y-1" data-testid="cycle-fix-preview">
            <div>Members to refund: <strong className="gold-text">{cycleFix.members_refunded}</strong></div>
            <div>Total to refund to Game Funds: <strong className="gold-text">${Number(cycleFix.total_member_refund).toFixed(2)}</strong></div>
            <div>To be removed from LP pool: <strong>${Number(cycleFix.lp_removed).toFixed(2)}</strong> (of ${Number(cycleFix.lp_credit_total).toFixed(2)} credited)</div>
            {cycleFix.per_user && Object.keys(cycleFix.per_user).length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/10">
                <div className="overline mb-1">Members getting refund</div>
                <div className="max-h-48 overflow-y-auto divide-y divide-white/5" data-testid="cycle-fix-member-list">
                  {Object.entries(cycleFix.per_user)
                    .sort((a, b) => b[1] - a[1])
                    .map(([uno, amt]) => (
                      <div key={uno} className="flex items-center justify-between py-1" data-testid={`cycle-fix-member-${uno}`}>
                        <span className="tabular text-neutral-200">ID {uno}</span>
                        <span className="tabular gold-text">+${Number(amt).toFixed(2)}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3 flex-wrap">
          <button onClick={previewCycleFix} disabled={busy} className="lt-btn-secondary disabled:opacity-50" data-testid="cycle-fix-preview-btn">
            {busy ? "Working…" : "Preview (dry run)"}
          </button>
          {cycleFix && (cycleFix.members_refunded > 0 || cycleFix.lp_removed > 0) && (
            <button onClick={applyCycleFix} disabled={busy} className="lt-btn-primary disabled:opacity-50" data-testid="cycle-fix-apply-btn">
              {busy ? "Working…" : `Apply — refund ${cycleFix.members_refunded} member(s)`}
            </button>
          )}
        </div>
        {msg.text && (
          <div className={`text-sm ${msg.type === "ok" ? "text-emerald-400" : "text-red-400"}`} data-testid="cycle-fix-msg">{msg.text}</div>
        )}
      </div>

      {/* Box cooldown setter + restart */}
      <div className="lt-card p-6 max-w-2xl space-y-4" data-testid="admin-restart-cooldown-card">
        <div>
          <div className="overline">Play Box Timer</div>
          <h3 className="font-display text-2xl mt-1">Box-to-box cooldown</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Set how long members must wait between boxes. Saving <strong>applies immediately</strong>
            to everyone (the timer restarts now). This is stored in the database — no redeploy or
            environment changes needed.
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="overline block mb-1">Hours</label>
            <input type="number" min="0" max="167" value={cdHours} onChange={(e) => setCdHours(e.target.value)} className="lt-input w-24 tabular" data-testid="cooldown-hours" />
          </div>
          <div>
            <label className="overline block mb-1">Minutes</label>
            <input type="number" min="0" max="59" value={cdMins} onChange={(e) => setCdMins(e.target.value)} className="lt-input w-24 tabular" data-testid="cooldown-mins" />
          </div>
          <button onClick={saveCooldown} disabled={busy} className="lt-btn-primary disabled:opacity-50" data-testid="cooldown-save-btn">
            {busy ? "Working…" : "Save & apply now"}
          </button>
          <button onClick={restartCooldown} disabled={busy} className="lt-btn-secondary disabled:opacity-50" data-testid="restart-cooldown-btn">
            Restart timer
          </button>
        </div>
        {msg.text && <div className={`text-sm ${msg.type === "ok" ? "text-emerald-400" : "text-red-400"}`} data-testid="restart-cooldown-msg">{msg.text}</div>}
      </div>

      {/* Reconciliation report — find members charged $5 on a failed drop */}
      <div className="lt-card p-6 space-y-4" data-testid="admin-reconcile-card">
        <div>
          <div className="overline">Play Card Diagnostics</div>
          <h3 className="font-display text-2xl mt-1">Failed-drop reconciliation</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Lists members whose <strong>card-drop request errored after their $5 was charged</strong>
            (they saw &quot;Something went wrong&quot;). Detected as <span className="font-mono">drops_count − history_rows</span>.
            If <strong>In-play principal</strong> covers the charge, the $5 is still parked in an open box and
            refunds 100% on box close (not lost).
          </p>
        </div>
        <button onClick={runReconcile} disabled={busy} className="lt-btn-primary" data-testid="reconcile-run-btn">
          {busy ? "Scanning…" : "Run reconciliation report"}
        </button>
        {reconcile && (
          <div className="space-y-3" data-testid="reconcile-result">
            <div className="text-sm text-neutral-300">
              <span className="gold-text tabular">{reconcile.affected_count}</span> affected member(s) ·
              <span className="gold-text tabular"> {reconcile.total_failed_drops}</span> failed drops ·
              <span className="gold-text tabular"> ${reconcile.total_amount_charged}</span> total charged
            </div>
            {reconcile.affected_count === 0 ? (
              <div className="text-sm text-emerald-400">No discrepancies found — every charged drop has a matching history row.</div>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="lt-table">
                  <thead>
                    <tr><th>User ID</th><th>Name</th><th>Mobile</th><th>Failed drops</th><th>Charged</th><th>In-play</th><th>Game Fund</th></tr>
                  </thead>
                  <tbody>
                    {reconcile.affected_members.map((m) => (
                      <tr key={m.user_id} data-testid={`reconcile-row-${m.user_no}`}>
                        <td className="tabular">{m.user_no}</td>
                        <td>{m.full_name}</td>
                        <td className="tabular text-neutral-400 text-xs">{m.mobile}</td>
                        <td className="tabular">{m.failed_drops}</td>
                        <td className="tabular gold-text">${m.amount_charged}</td>
                        <td className="tabular">${m.in_play_principal}</td>
                        <td className="tabular text-neutral-400">${m.game_fund_balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(reconcile.unresolved_payout_failures || []).length > 0 && (
              <div className="text-xs text-amber-300/90" data-testid="reconcile-payout-fails">
                ⚠ {reconcile.unresolved_payout_failures.length} box-close payout failure(s) logged since the fix shipped — review for partial box payouts.
              </div>
            )}
          </div>
        )}

        <div className="pt-4 mt-2 border-t border-white/5 space-y-3">
          <div className="overline">Check a specific member</div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              className="lt-input max-w-[200px]"
              placeholder="User ID e.g. 100003"
              value={auditNo}
              onChange={(e) => setAuditNo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runAudit(); }}
              data-testid="audit-userid-input"
            />
            <button onClick={runAudit} disabled={busy} className="lt-btn-secondary" data-testid="audit-run-btn">
              {busy ? "Checking…" : "Audit member"}
            </button>
          </div>
          {audit && (
            <div className={`rounded border p-4 text-sm space-y-1 ${audit.money_fully_accounted ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="audit-result">
              <div className="font-display text-lg">{audit.full_name} <span className="tabular text-neutral-400">#{audit.user_no}</span></div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-neutral-300">
                <div>Cards dropped: <span className="tabular gold-text">{audit.drops_count}</span></div>
                <div>History rows: <span className="tabular gold-text">{audit.history_rows}</span></div>
                <div>Total charged: <span className="tabular">${audit.total_charged_on_drops}</span></div>
                <div>In-play (refunds on close): <span className="tabular">${audit.in_play_principal}</span></div>
                <div>Game Fund now: <span className="tabular">${audit.game_fund_balance}</span></div>
                <div>Investor earned: <span className="tabular">${audit.investor_income_earned}</span></div>
              </div>
              <div className={`mt-2 ${audit.money_fully_accounted ? "text-emerald-400" : "text-red-400"}`} data-testid="audit-verdict">
                {audit.money_fully_accounted ? "✓ " : "⚠ "}{audit.note}
              </div>
              {(audit.releasable_principal ?? audit.in_play_principal) > 0 && (
                <div className="pt-3 mt-2 border-t border-white/10">
                  <div className="text-xs text-neutral-400 mb-2">
                    If this member can't play because their funds are stuck in principal (a box payout that
                    didn't refund), release it back into their Game Fund:
                  </div>
                  <button onClick={releasePrincipal} disabled={busy} className="lt-btn-primary text-sm" data-testid="release-principal-btn">
                    {busy ? "Releasing…" : `Release $${audit.releasable_principal ?? audit.in_play_principal} to Game Fund`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Transfer Play Card principal → Game Fund for a chosen member */}
      <div className="lt-card p-6 space-y-4" data-testid="admin-transfer-principal-card">
        <div>
          <div className="overline">Play Card Recovery</div>
          <h3 className="font-display text-2xl mt-1">Transfer Principal → Game Fund</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Move a member&apos;s <strong>in-play Play Card principal</strong> back into their
            <strong> Game Fund</strong> (NFT Card Fund) so they can keep playing. Enter the member&apos;s
            User ID — you&apos;ll be shown the exact amount to confirm before it moves. This clears their
            open box stakes and cannot be undone.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            className="lt-input max-w-[200px]"
            placeholder="User ID e.g. 100003"
            value={principalNo}
            onChange={(e) => setPrincipalNo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") transferPrincipalToGameFund(); }}
            data-testid="transfer-principal-userid-input"
          />
          <button
            onClick={transferPrincipalToGameFund}
            disabled={busy || !principalNo.trim()}
            className="lt-btn-primary"
            data-testid="transfer-principal-btn"
          >
            {busy ? "Transferring…" : "Transfer to Game Fund"}
          </button>
        </div>
      </div>

      {/* Play Card cap recycle — $5 → LP Pool per $15 of investor income earned */}
      <div className="lt-card p-6 space-y-4" data-testid="admin-cap-settle-card">
        <div>
          <div className="overline">Play Card Cap Recycle</div>
          <h3 className="font-display text-2xl mt-1">Recycle capped members</h3>
          <p className="text-sm text-neutral-400 mt-2">
            For every full <strong>$15</strong> of investor income a member earns, <strong>$5</strong> of their
            parked principal moves from their Game Fund to the system LP Pool (e.g. $87 earned → $25 moved).
            New box closes do this automatically — use this to catch up members who earned <strong>before</strong>
            the rule shipped. Members are <strong>not</strong> deactivated and keep playing.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => runSettleCaps(false)} disabled={busy} className="lt-btn-secondary" data-testid="cap-preview-btn">
            {busy ? "Scanning…" : "Preview affected members"}
          </button>
          {capSettle && capSettle.members_affected > 0 && (
            <button onClick={() => runSettleCaps(true)} disabled={busy} className="lt-btn-primary" data-testid="cap-apply-btn">
              Move ${capSettle.total_principal_to_lp} to LP Pool ({capSettle.members_affected})
            </button>
          )}
        </div>
        {capSettle && (
          <div data-testid="cap-settle-result" className="space-y-2">
            <div className="text-sm text-neutral-300">
              <span className="gold-text tabular">{capSettle.members_affected}</span> member(s) ·
              <span className="gold-text tabular"> ${capSettle.total_principal_to_lp}</span> to move ·
              <span className={capSettle.applied ? "text-emerald-400" : "text-amber-300"}> {capSettle.applied ? "APPLIED" : "preview"}</span>
            </div>
            {capSettle.members_affected === 0 ? (
              <div className="text-sm text-emerald-400">All members are fully recycled — nothing owed.</div>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="lt-table">
                  <thead><tr><th>User ID</th><th>Name</th><th>Investor earned</th><th>Already recycled</th><th>Game Fund</th><th>→ LP now</th></tr></thead>
                  <tbody>
                    {capSettle.members.map((m) => (
                      <tr key={m.user_no} data-testid={`cap-row-${m.user_no}`}>
                        <td className="tabular">{m.user_no}</td>
                        <td>{m.full_name}</td>
                        <td className="tabular">${m.investor_earned}</td>
                        <td className="tabular text-neutral-400">${m.already_recycled}</td>
                        <td className="tabular text-neutral-400">${m.game_fund_balance}</td>
                        <td className="tabular gold-text">${m.to_move_to_lp}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Soft reset (Row 54 / Q5a) */}
      <div className="lt-card p-6 max-w-2xl space-y-4" data-testid="admin-system-stale-card">
        <div>
          <div className="overline">System Actions</div>
          <h3 className="font-display text-2xl mt-1">Reset stale boxes (soft reset)</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Use this when boxes are not getting fulfilled and you want to start a fresh round
            <strong> without burning users&apos; parked principal</strong>. The game restarts
            from <strong>Cycle 1 / Box 1</strong>.
          </p>
          <p className="text-xs text-amber-300/80 mt-2">
            ⏱ Can be run <strong>anytime</strong> at the admin&apos;s discretion — no waiting
            period. Use it whenever the board is stuck or you want a fresh round.
          </p>
          <p className="text-xs text-emerald-300/80 mt-2">
            Each user with unfilled box payments will see a <strong>Claim</strong> button in their
            Play Card section. When they click it, the parked principal is refunded to their
            Game Fund (LP wallet). Wallets are NOT touched, 300%-cap counters are NOT touched.
          </p>
        </div>
        {!confirmStale ? (
          <button onClick={() => setConfirmStale(true)} className="lt-btn-primary" data-testid="play-reset-stale-open-btn">
            Reset stale boxes
          </button>
        ) : (
          <div className="space-y-3 p-4 rounded border border-yellow-500/30 bg-yellow-500/5">
            <div className="text-sm text-yellow-200">
              Type <span className="font-mono tabular">RESET</span> to confirm a soft reset.
            </div>
            <ConfirmReset onConfirm={resetStale} onCancel={() => setConfirmStale(false)} busy={busy} />
          </div>
        )}
      </div>

      {/* Hard reset (existing) */}
      <div className="lt-card p-6 max-w-2xl space-y-4" data-testid="admin-system-tab">
        <div>
          <div className="overline">DANGER ZONE</div>
          <h3 className="font-display text-2xl mt-1">Hard reset — wipe everything</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Wipes <strong>every</strong> user&apos;s Play Card progress: total drops, current cycle/box, principal, box payments, and history.
            Also resets the lifetime <strong>300% cap</strong> counters (<span className="tabular">play_principal_total</span> &amp; <span className="tabular">play_investor_total</span>).
            Parked principals are <strong>lost</strong> — users do NOT get a Claim button.
          </p>
          <p className="text-xs text-amber-300/80 mt-2">
            ⚠ This is irreversible. Wallet balances (Main / LP / Deposit / Rebirth) are NOT touched.
          </p>
        </div>
        {!confirm ? (
          <button onClick={() => setConfirm(true)} className="lt-btn-secondary" data-testid="play-reset-open-btn">
            Reset all Play Card data
          </button>
        ) : (
          <div className="space-y-3 p-4 rounded border border-red-500/30 bg-red-500/5">
            <div className="text-sm text-red-300">Type <span className="font-mono tabular">RESET</span> to confirm.</div>
            <ConfirmReset onConfirm={resetAll} onCancel={() => setConfirm(false)} busy={busy} />
          </div>
        )}
        {msg.text && <div className={`text-sm ${msg.type === "ok" ? "gold-text" : "text-red-400"}`} data-testid="play-reset-msg">{msg.text}</div>}
      </div>

      {/* Factory Reset — Nuclear option for "Final Human Testing" */}
      <div className="lt-card p-6 max-w-2xl space-y-4 border border-red-500/40" data-testid="admin-factory-reset-card">
        <div>
          <div className="overline text-red-400">NUCLEAR ZONE</div>
          <h3 className="font-display text-2xl mt-1 text-red-300">Factory Reset — wipe entire platform</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Wipes <strong>every</strong> non-admin user, every wallet, every history & queue,
            and resets <span className="tabular">system_funds</span> + <span className="tabular">play_card_global</span> to zero.
            Use this <strong>once</strong> before Final Human Testing to start completely fresh.
          </p>
          <p className="text-xs text-red-300/80 mt-2">
            ⚠ Irreversible. The admin account (<span className="tabular">admin@ltglobal.club</span>) is preserved
            and its main wallet is reseeded from <span className="tabular">ADMIN_SEED_BALANCE</span> env.
          </p>
          <ul className="text-xs text-neutral-500 mt-2 list-disc list-inside space-y-1">
            <li>Deletes: users (non-admin), wallets, play_card, history, queues, income, withdrawals, donation, lapses, subscriptions, activation requests, transfers</li>
            <li>Resets: play_card_global counters, all system_funds buckets to $0</li>
            <li>Keeps: admin user + Admin Fund (reseeded)</li>
          </ul>
        </div>
        {!confirmFactory ? (
          <button
            onClick={() => setConfirmFactory(true)}
            className="lt-btn-secondary !border-red-500/60 !text-red-300 hover:!bg-red-500/10"
            data-testid="factory-reset-open-btn"
          >
            Factory Reset Entire System
          </button>
        ) : (
          <div className="space-y-3 p-4 rounded border border-red-500/50 bg-red-500/10">
            <div className="text-sm text-red-200">
              This will <strong>permanently delete</strong> all user data. Type
              <span className="font-mono tabular"> WIPE </span> to confirm.
            </div>
            <ConfirmFactoryReset onConfirm={factoryReset} onCancel={() => setConfirmFactory(false)} busy={busy} />
          </div>
        )}
      </div>
    </div>
  );
}

function ConfirmFactoryReset({ onConfirm, onCancel, busy }) {
  const [text, setText] = React.useState("");
  return (
    <div className="space-y-3">
      <input
        type="text" className="lt-input"
        placeholder="Type WIPE to confirm"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="factory-reset-confirm-input"
        autoFocus
      />
      <div className="flex gap-2">
        <button onClick={onCancel} className="lt-btn-secondary" disabled={busy} data-testid="factory-reset-cancel-btn">Cancel</button>
        <button
          onClick={onConfirm}
          className="lt-btn-primary !bg-red-600 hover:!bg-red-500"
          disabled={busy || text !== "WIPE"}
          data-testid="factory-reset-confirm-btn"
        >
          {busy ? "Wiping…" : "Confirm Factory Reset"}
        </button>
      </div>
    </div>
  );
}

function ConfirmReset({ onConfirm, onCancel, busy }) {
  const [text, setText] = React.useState("");
  return (
    <div className="space-y-3">
      <input
        type="text" className="lt-input"
        placeholder="Type RESET to confirm"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="play-reset-confirm-input"
        autoFocus
      />
      <div className="flex gap-2">
        <button onClick={onCancel} className="lt-btn-secondary" disabled={busy} data-testid="play-reset-cancel-btn">Cancel</button>
        <button onClick={onConfirm} className="lt-btn-primary" disabled={busy || text !== "RESET"} data-testid="play-reset-confirm-btn">
          {busy ? "Resetting…" : "Confirm Reset"}
        </button>
      </div>
    </div>
  );
}
